<template>
  <el-drawer
    :title="isEval ? '评估任务详情' : '训练任务详情'"
    :visible.sync="innerVisible"
    size="760px"
    @open="onOpen"
    @closed="onClosed"
  >
    <div v-if="run" class="detail-body">
      <div class="detail-title">
        <span class="mono">{{ run.run_name }}</span>
        <el-tag :type="sourceMeta(run.source).tag" size="mini" class="title-tag">
          {{ sourceMeta(run.source).label }}
        </el-tag>
      </div>

      <el-descriptions title="基本信息" :column="2" size="small" border>
        <el-descriptions-item label="状态">
          <el-tag :type="stateMeta(row).tag" size="mini">
            {{ stateMeta(row).label }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="退出码">{{ dash(run.exit_code) }}</el-descriptions-item>
        <el-descriptions-item label="派发人">{{ dash(run.dispatched_by) }}</el-descriptions-item>
        <el-descriptions-item label="派发时间">{{ formatTime(run.dispatched_at) }}</el-descriptions-item>
        <el-descriptions-item label="结束时间">{{ formatTime(run.finished_at) }}</el-descriptions-item>
        <el-descriptions-item label="停止人">{{ dash(run.stopped_by) }}</el-descriptions-item>
        <el-descriptions-item label="容器">
          <span class="mono">{{ dash(run.container_name) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="容器状态">{{ dash(run.container_status) }}</el-descriptions-item>
      </el-descriptions>

      <el-descriptions
        :title="isEval ? '评估参数' : '训练参数'"
        :column="2"
        size="small"
        border
        class="section-gap"
      >
        <el-descriptions-item label="数据集">
          <span class="mono">{{ dash(run.out_name) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="基础权重">
          <span class="mono">{{ dash(run.model_name || run.model_id) }}</span>
        </el-descriptions-item>
        <el-descriptions-item v-if="!isEval" label="训练轮数">{{ dash(run.epochs) }}</el-descriptions-item>
        <el-descriptions-item v-if="!isEval" label="batch / imgsz / workers">
          {{ dash(run.batch) }} / {{ dash(run.imgsz) }} / {{ dash(run.workers) }}
        </el-descriptions-item>
        <el-descriptions-item label="使用显卡" :span="2">
          <template v-if="(run.gpu_ids || []).length">
            <el-tag v-for="id in run.gpu_ids" :key="id" size="mini" class="gpu-tag">卡 {{ id }}</el-tag>
          </template>
          <span v-else class="muted">-</span>
        </el-descriptions-item>
      </el-descriptions>

      <div v-if="!isEval" class="section-gap">
        <div class="block-header">
          <span class="section-title">训练进度</span>
          <span class="block-note">最近产物更新：{{ relativeTime(run.last_modified) }}</span>
        </div>
        <el-progress
          :percentage="progressPercent"
          :status="progressStatus"
          :stroke-width="14"
        />
        <div class="progress-text">
          <span v-if="progress.epochs_total">
            已完成 {{ progress.epochs_done }} / {{ progress.epochs_total }} 轮
          </span>
          <span v-else>还没有轮次</span>
          <span v-if="lastRowItems.length" class="block-note">
            最近一轮：{{ lastRowText }}
          </span>
        </div>
      </div>

      <div v-if="!isEval" class="section-gap">
        <div class="block-header">
          <span class="section-title">每轮指标</span>
          <span v-if="run.state === 'running'" class="block-note">训练中，每 10 秒自动刷新</span>
        </div>
        <training-metrics-chart
          v-loading="metricsLoading"
          :columns="metricsColumns"
          :rows="metricsRows"
          :rows-total="metricsRowsTotal"
          :truncated="metricsTruncated"
        />
      </div>

      <div class="section-gap">
        <div class="block-header">
          <span class="section-title">产物清单</span>
          <div>
            <el-tag :type="artifacts.complete ? 'success' : 'info'" size="mini">
              {{ artifacts.complete ? artifactsCompleteLabel : artifactsIncompleteLabel }}
            </el-tag>
            <el-button
              size="mini"
              type="primary"
              plain
              class="head-button"
              @click="artifactsVisible = true"
            >{{ artifactButtonText }}</el-button>
          </div>
        </div>
        <div class="artifact-box">
          <template v-for="name in artifacts.present">
            <el-tag :key="`p-${name}`" type="success" size="mini" class="artifact-tag">
              {{ name }}
            </el-tag>
          </template>
          <template v-for="name in artifacts.missing">
            <el-tag :key="`m-${name}`" type="info" size="mini" class="artifact-tag">
              {{ name }}
            </el-tag>
          </template>
        </div>
      </div>

      <div class="section-gap">
        <div class="block-header">
          <span class="section-title">归档到 MinIO</span>
          <div>
            <el-tag :type="pushMeta(run.push_state).tag" size="mini">
              {{ pushMeta(run.push_state).label }}
            </el-tag>
            <el-button
              v-if="run.archivable"
              size="mini"
              type="primary"
              plain
              :loading="pushing"
              class="head-button"
              @click="handlePush"
            >归档产物</el-button>
          </div>
        </div>
        <div class="push-line">归档时间：{{ formatTime(run.push_at) }}</div>
        <div v-if="run.push_message" class="push-message">{{ run.push_message }}</div>
      </div>

      <div class="section-gap">
        <div class="block-header">
          <span class="section-title">{{ isEval ? '评估日志' : '训练日志' }}</span>
          <div class="log-tools">
            <el-select v-model="tail" size="mini" class="tail-select" @change="loadLog">
              <el-option v-for="n in TAIL_OPTIONS" :key="n" :label="`末 ${n} 行`" :value="n" />
            </el-select>
            <el-button size="mini" :loading="logLoading" @click="loadLog">刷新</el-button>
          </div>
        </div>
        <!-- 日志尾每 3 秒自动刷新，抽屉关掉就停 -->
        <pre v-loading="logLoading" class="log-box">{{ logText || '还没有日志' }}</pre>
      </div>

      <div class="drawer-footer">
        <el-button size="small" @click="innerVisible = false">关闭</el-button>
      </div>
    </div>

    <!-- 全部产物：清单在训练机上、弹层打开时现拉；下载走浏览器直链 -->
    <el-dialog
      title="全部产物"
      :visible.sync="artifactsVisible"
      width="680px"
      append-to-body
      @open="loadArtifacts"
    >
      <el-table
        v-loading="artifactsLoading"
        :data="artifactItems"
        size="mini"
        max-height="420"
        @selection-change="onArtifactSelect"
      >
        <el-table-column type="selection" width="42" />
        <el-table-column label="文件" prop="path" show-overflow-tooltip />
        <el-table-column label="大小" width="100" align="right">
          <template slot-scope="{ row }">{{ formatSize(row.size) }}</template>
        </el-table-column>
        <el-table-column label="修改时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.mtime) }}</template>
        </el-table-column>
        <el-table-column label="" width="70" align="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="downloadArtifact(row)">下载</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="artifact-total">
        共 {{ artifactItems.length }} 件，{{ formatSize(artifactTotalBytes) }}
      </div>
      <div slot="footer">
        <el-button size="small" @click="artifactsVisible = false">关闭</el-button>
        <el-button
          size="small"
          type="primary"
          :disabled="!artifactItems.length"
          @click="downloadArchive"
        >{{ selectedArtifacts.length ? `下载所选（${selectedArtifacts.length}）` : '下载全部' }}</el-button>
      </div>
    </el-dialog>
  </el-drawer>
</template>

<script>
import dayjs from 'dayjs';
import TrainingMetricsChart from './TrainingMetricsChart.vue';
import {
  fetchRunArtifacts,
  fetchRunDetail,
  fetchRunLog,
  fetchRunMetrics,
  pushRun,
  runArtifactDownloadUrl,
  runArtifactsArchiveUrl,
} from '../api/blissrose/training';
import {
  ARCHIVE_STATES,
  EVAL_ARCHIVED,
  EVAL_STATES,
  EVAL_STOPPING,
  TRAIN_STATES,
  TRAIN_STOPPING,
} from '../utils/labels';

// 日志自动刷新间隔
const LOG_POLL_INTERVAL_MS = 3000;
// 指标曲线刷新间隔；与任务列表同一节奏
const METRICS_POLL_INTERVAL_MS = 10000;
const TAIL_OPTIONS = [200, 500, 1000, 5000];

const SOURCE_META = {
  platform: { label: '平台派发', tag: 'primary' },
};

// results.csv 里最值得一眼看到的几列
const LAST_ROW_KEYS = [
  'epoch',
  'metrics/mAP50(B)',
  'metrics/mAP50-95(B)',
  'train/box_loss',
  'train/seg_loss',
  'val/box_loss',
  'val/seg_loss',
];

/** 训练任务详情抽屉：打开先用列表行渲染、再拉详情校正；日志与指标只在打开期间轮询 */
export default {
  name: 'TrainingDetailDrawer',

  components: { TrainingMetricsChart },

  props: {
    visible: { type: Boolean, default: false },
    /** 列表行，来自任务列表；为空时不渲染内容 */
    run: { type: Object, default: null },
  },

  data() {
    return {
      TAIL_OPTIONS,
      detail: null,
      pushing: false,
      logLoading: false,
      logText: '',
      tail: TAIL_OPTIONS[0],
      logTimer: null,
      metrics: null,
      metricsLoading: false,
      metricsTimer: null,
      artifactsVisible: false,
      artifactsLoading: false,
      artifactItems: [],
      artifactTotalBytes: 0,
      // 清单总数；null＝还没拉到
      artifactCount: null,
      selectedArtifacts: [],
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },

    /** 详情拉到之前先用列表行渲染，拉到之后以详情为准；列表行独有的归档标记跟着保留 */
    row() {
      return { ...(this.run || {}), ...(this.detail || {}) };
    },

    /** 评估行：没有训练轮次与指标曲线，文案与轮询相应换一套 */
    isEval() {
      return this.row.run_type === 'eval';
    },

    runName() {
      return (this.run && this.run.run_name) || '';
    },

    progress() {
      return this.row.progress || {};
    },

    artifacts() {
      return this.row.artifacts || { present: [], missing: [], complete: false };
    },

    progressPercent() {
      const { percent } = this.progress;
      return typeof percent === 'number' ? percent : 0;
    },

    /** 百分比由训练机算；这里只把状态翻成进度条颜色 */
    progressStatus() {
      if (this.row.state === 'failed') return 'exception';
      if (this.row.state === 'finished') return 'success';
      return null;
    },

    lastRowItems() {
      const last = this.progress.last_row || {};
      return LAST_ROW_KEYS.filter(key => last[key] !== undefined && last[key] !== '');
    },

    lastRowText() {
      const last = this.progress.last_row || {};
      return this.lastRowItems.map(key => `${key.split('/').pop()}=${last[key]}`).join('  ');
    },

    metricsColumns() {
      return (this.metrics && this.metrics.columns) || [];
    },

    metricsRows() {
      return (this.metrics && this.metrics.rows) || [];
    },

    metricsRowsTotal() {
      return (this.metrics && this.metrics.rows_total) || 0;
    },

    metricsTruncated() {
      return Boolean(this.metrics && this.metrics.truncated);
    },

    artifactButtonText() {
      return this.artifactCount === null ? '全部产物' : `全部产物（${this.artifactCount}）`;
    },

    artifactsCompleteLabel() {
      return this.isEval ? '报告齐全' : '权重齐全';
    },

    artifactsIncompleteLabel() {
      return this.isEval ? '产物不全' : '权重不全';
    },
  },

  beforeDestroy() {
    this.stopLogPolling();
    this.stopMetricsPolling();
  },

  methods: {
    onOpen() {
      this.loadDetail();
      this.loadLog();
      this.loadArtifacts();
      this.stopLogPolling();
      this.logTimer = setInterval(() => {
        if (!this.logLoading) this.loadLog(true);
      }, LOG_POLL_INTERVAL_MS);
      this.stopMetricsPolling();
      // 评估没有训练曲线：不拉指标也不起轮询
      if (!this.isEval) {
        this.loadMetrics();
        this.metricsTimer = setInterval(() => this.pollMetrics(), METRICS_POLL_INTERVAL_MS);
      }
    },

    onClosed() {
      this.stopLogPolling();
      this.stopMetricsPolling();
      this.detail = null;
      this.logText = '';
      this.metrics = null;
      this.tail = TAIL_OPTIONS[0];
      this.artifactsVisible = false;
      this.artifactItems = [];
      this.artifactTotalBytes = 0;
      this.artifactCount = null;
      this.selectedArtifacts = [];
    },

    stopLogPolling() {
      if (this.logTimer) {
        clearInterval(this.logTimer);
        this.logTimer = null;
      }
    },

    stopMetricsPolling() {
      if (this.metricsTimer) {
        clearInterval(this.metricsTimer);
        this.metricsTimer = null;
      }
    },

    /** 训练中才轮询指标；一并刷新一次详情 */
    pollMetrics() {
      if (this.metricsLoading || this.row.state !== 'running') return;
      this.loadMetrics(true);
      this.loadDetail();
    },

    /** 拉每轮指标；silent 为自动刷新，不显示加载态 */
    async loadMetrics(silent = false) {
      if (!this.runName) return;
      if (!silent) this.metricsLoading = true;
      try {
        this.metrics = (await fetchRunMetrics(this.runName)).data;
      } catch (err) {
        // 失败由响应拦截器统一提示，保留上一次的曲线
      } finally {
        this.metricsLoading = false;
      }
    },

    /** 拉详情校正列表行到最新 */
    async loadDetail() {
      if (!this.runName) return;
      try {
        const { item } = (await fetchRunDetail(this.runName)).data;
        this.detail = item;
        this.$emit('updated', item);
      } catch (err) {
        // 错误提示由响应拦截器统一弹出，保留列表行
      }
    },

    /** 拉日志尾；silent 为自动刷新，不显示加载态 */
    async loadLog(silent = false) {
      if (!this.runName) return;
      if (!silent) this.logLoading = true;
      try {
        const { log } = (await fetchRunLog(this.runName, this.tail)).data;
        this.logText = log || '';
      } catch (err) {
        // 容器记录被清掉后会持续失败，保留上一次的日志
      } finally {
        this.logLoading = false;
      }
    },

    async handlePush() {
      try {
        await this.$confirm(
          '归档会把产物上传到 MinIO，已存在的对象会跳过，只补缺的，传输期间请勿关闭页面。确定归档？',
          '归档产物',
          { type: 'warning' },
        );
      } catch (err) {
        return;
      }
      this.pushing = true;
      try {
        // 不覆盖，重复归档只补缺的
        const { message } = (await pushRun(this.runName, false)).data;
        this.$message.success(message || '已归档');
        await this.loadDetail();
      } catch (err) {
        // 失败原因由响应拦截器弹出
      } finally {
        this.pushing = false;
      }
    },

    // ====== 产物下载 ======

    /** 产物清单：抽屉打开时拿一次给按钮报数，弹层打开时再拿一次 */
    async loadArtifacts() {
      if (!this.runName) return;
      this.artifactsLoading = true;
      try {
        const data = (await fetchRunArtifacts(this.runName)).data;
        this.artifactItems = data.items || [];
        this.artifactTotalBytes = data.total_bytes || 0;
        this.artifactCount = this.artifactItems.length;
        this.selectedArtifacts = [];
      } catch (err) {
        // 失败由响应拦截器统一提示；清单留空
      } finally {
        this.artifactsLoading = false;
      }
    },

    onArtifactSelect(rows) {
      this.selectedArtifacts = rows.map(row => row.path);
    },

    /** 浏览器直链下载：不经过 axios（大文件不缓冲），同站 cookie 过鉴权 */
    triggerDownload(url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    downloadArtifact(row) {
      this.triggerDownload(runArtifactDownloadUrl(this.runName, row.path));
    },

    /** 打包下载：勾了就打勾选的几件，没勾就打全部 */
    downloadArchive() {
      this.triggerDownload(runArtifactsArchiveUrl(this.runName, this.selectedArtifacts));
    },

    // ====== 展示辅助 ======

    /** 停止请求已登记但容器还在跑；与列表判定规则一致 */
    isStopping() {
      return this.row.state === 'running' && Boolean(this.row.stopped_by);
    },

    stateMeta(row) {
      if (this.isEval) {
        if (this.row.archived) return EVAL_ARCHIVED;
        if (this.isStopping()) return EVAL_STOPPING;
        return EVAL_STATES[this.row.state] || EVAL_STATES.unknown;
      }
      if (this.isStopping()) return TRAIN_STOPPING;
      return TRAIN_STATES[row.state] || TRAIN_STATES.unknown;
    },

    pushMeta(state) {
      return ARCHIVE_STATES[state] || { label: state || '未归档', tag: 'info' };
    },

    sourceMeta(source) {
      // 来源只有平台派发一种，认不出的值也按它显示
      return SOURCE_META[source] || SOURCE_META.platform;
    },

    /** 0 与 false 都是有效值，只有 null / undefined / 空串按缺省显示 */
    dash(value) {
      return value === null || value === undefined || value === '' ? '-' : value;
    },

    formatTime(value) {
      return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
    },

    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
      return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    },

    /** 「最近产物更新」显示相隔多久 */
    relativeTime(value) {
      if (!value) return '-';
      const mtime = dayjs(value);
      const minutes = dayjs().diff(mtime, 'minute');
      if (minutes < 1) return '刚刚';
      if (minutes < 60) return `${minutes} 分钟前`;
      const hours = dayjs().diff(mtime, 'hour');
      if (hours < 24) return `${hours} 小时前`;
      return `${dayjs().diff(mtime, 'day')} 天前（${mtime.format('MM-DD HH:mm')}）`;
    },
  },
};
</script>

<style scoped>
.detail-body {
  padding: 0 20px 20px;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 16px;
  word-break: break-all;
}

.title-tag {
  margin-left: 8px;
  font-weight: normal;
}

.section-gap {
  margin-top: 20px;
}

.block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.block-note {
  font-size: 12px;
  color: #909399;
}

.progress-text {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-size: 12px;
  color: #606266;
  margin-top: 8px;
}

.artifact-box {
  line-height: 1.8;
}

.artifact-tag {
  margin: 0 6px 4px 0;
}

.gpu-tag {
  margin: 0 6px 4px 0;
}

.head-button {
  margin-left: 8px;
}

.artifact-total {
  font-size: 12px;
  color: #606266;
  margin-top: 8px;
}

.push-line {
  font-size: 12px;
  color: #606266;
}

.push-message {
  font-size: 12px;
  color: #f56c6c;
  line-height: 1.6;
  margin-top: 6px;
  word-break: break-all;
}

.log-tools {
  display: flex;
  align-items: center;
}

.tail-select {
  width: 110px;
  margin-right: 8px;
}

.log-box {
  background: #1e1e1e;
  color: #d4d4d4;
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  line-height: 1.6;
  padding: 10px;
  margin: 0;
  height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.mono {
  font-family: Consolas, Monaco, monospace;
}

.muted {
  color: #c0c4cc;
}

.drawer-footer {
  margin-top: 20px;
  text-align: right;
}
</style>
